<template>
  <div class="site-management">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">
              <i class="fas fa-map-marker-alt mr-2"></i>
              Site Management
            </h4>
            <b-button variant="success" size="sm" @click="openCreateModal">
              <i class="fas fa-plus mr-1"></i> Add Site
            </b-button>
          </div>
          <div class="card-body">
            <div v-if="$apollo.loading" class="text-center">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>

            <div v-else>
              <div class="mb-3">
                <b-input-group>
                  <b-form-input
                    v-model="searchTerm"
                    placeholder="Search sites by name, key, type, city, or state..."
                    debounce="300"
                  />
                  <b-input-group-append>
                    <b-button variant="outline-secondary" @click="searchTerm = ''">
                      <i class="fa fa-times"></i>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>

              <div v-if="filteredSites.length" class="table-responsive">
                <table class="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th class="d-none d-sm-table-cell">Key</th>
                      <th class="d-none d-md-table-cell">Type</th>
                      <th class="d-none d-md-table-cell">City</th>
                      <th class="d-none d-lg-table-cell">State</th>
                      <th class="d-none d-sm-table-cell">Company</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="site in filteredSites" :key="site.id">
                      <td><strong>{{ site.name }}</strong></td>
                      <td class="d-none d-sm-table-cell"><code>{{ site.key }}</code></td>
                      <td class="d-none d-md-table-cell">{{ site.type || '—' }}</td>
                      <td class="d-none d-md-table-cell">{{ site.city || '—' }}</td>
                      <td class="d-none d-lg-table-cell">{{ site.state || '—' }}</td>
                      <td class="d-none d-sm-table-cell">
                        <b-badge v-if="site.company" variant="primary">{{ site.company.name }}</b-badge>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td class="text-right">
                        <b-dropdown size="sm" variant="outline-secondary" right no-caret>
                          <template #button-content>
                            <i class="fas fa-ellipsis-v"></i>
                          </template>
                          <b-dropdown-item @click="openEditModal(site)">
                            <i class="fas fa-edit mr-1"></i> Edit
                          </b-dropdown-item>
                          <b-dropdown-item variant="danger" @click="confirmDelete(site)">
                            <i class="fas fa-trash mr-1"></i> Delete
                          </b-dropdown-item>
                        </b-dropdown>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center text-muted py-3">
                <p v-if="searchTerm">No sites match your search.</p>
                <p v-else>No sites found. Click "Add Site" to create one.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / Edit Site Modal -->
    <b-modal
      v-model="showFormModal"
      :title="formMode === 'edit' ? 'Edit Site' : 'Add Site'"
      :ok-title="formMode === 'edit' ? 'Save Changes' : 'Create Site'"
      :ok-disabled="!isFormValid || saving"
      :cancel-disabled="saving"
      :no-close-on-backdrop="saving"
      :no-close-on-esc="saving"
      @ok="submitForm"
      @hidden="resetForm"
    >
      <b-form @submit.prevent="submitForm">
        <b-form-group label="Site Key *" label-for="site-key">
          <b-form-input
            id="site-key"
            v-model="form.key"
            placeholder="e.g. main-plant"
            required
            :disabled="saving"
          />
          <small class="form-text text-muted">Unique identifier for this site.</small>
        </b-form-group>

        <b-form-group label="Site Name *" label-for="site-name">
          <b-form-input
            id="site-name"
            v-model="form.name"
            placeholder="e.g. Main Bottling Plant"
            required
            :disabled="saving"
          />
        </b-form-group>

        <b-form-group label="Type" label-for="site-type">
          <b-form-input
            id="site-type"
            v-model="form.type"
            placeholder="Free-form (e.g. plant, well field, monitoring)"
            :disabled="saving"
          />
        </b-form-group>

        <b-row>
          <b-col md="7">
            <b-form-group label="City" label-for="site-city">
              <b-form-input id="site-city" v-model="form.city" :disabled="saving" />
            </b-form-group>
          </b-col>
          <b-col md="5">
            <b-form-group label="State" label-for="site-state">
              <b-form-input id="site-state" v-model="form.state" maxlength="2" placeholder="e.g. ME" :disabled="saving" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Company *" label-for="site-company">
          <b-form-select
            id="site-company"
            v-model="form.companyKey"
            :options="companyOptions"
            required
            :disabled="saving"
          />
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  SITES_MANAGEMENT_QUERY,
  COMPANIES_QUERY,
  CREATE_SITE_MUTATION,
  UPDATE_SITE_MUTATION,
  DELETE_SITE_MUTATION
} from '@/constants/graphql'

export default {
  name: 'SiteManagement',
  data () {
    return {
      searchTerm: '',
      showFormModal: false,
      formMode: 'create',
      saving: false,
      editingId: null,
      form: {
        key: '',
        name: '',
        type: '',
        city: '',
        state: '',
        companyKey: null
      }
    }
  },
  apollo: {
    sites: {
      query: SITES_MANAGEMENT_QUERY,
      fetchPolicy: 'cache-and-network'
    },
    companies: {
      query: COMPANIES_QUERY
    }
  },
  computed: {
    filteredSites () {
      if (!this.sites) return []
      if (!this.searchTerm) return this.sites
      const term = this.searchTerm.toLowerCase()
      return this.sites.filter(site => {
        return (site.name && site.name.toLowerCase().includes(term)) ||
          (site.key && site.key.toLowerCase().includes(term)) ||
          (site.type && site.type.toLowerCase().includes(term)) ||
          (site.city && site.city.toLowerCase().includes(term)) ||
          (site.state && site.state.toLowerCase().includes(term))
      })
    },
    companyOptions () {
      const base = [{ value: null, text: 'Select a company...' }]
      if (!this.companies) return base
      return base.concat(this.companies.map(c => ({ value: c.key, text: c.name })))
    },
    isFormValid () {
      return !!(
        this.form.key && this.form.key.trim() &&
        this.form.name && this.form.name.trim() &&
        this.form.companyKey
      )
    }
  },
  methods: {
    openCreateModal () {
      this.formMode = 'create'
      this.editingId = null
      this.form = { key: '', name: '', type: '', city: '', state: '', companyKey: null }
      this.showFormModal = true
    },
    openEditModal (site) {
      this.formMode = 'edit'
      this.editingId = site.id
      this.form = {
        key: site.key || '',
        name: site.name || '',
        type: site.type || '',
        city: site.city || '',
        state: site.state || '',
        companyKey: (site.company && site.company.key) || null
      }
      this.showFormModal = true
    },
    resetForm () {
      this.formMode = 'create'
      this.editingId = null
      this.form = { key: '', name: '', type: '', city: '', state: '', companyKey: null }
      this.saving = false
    },
    buildInput () {
      const input = {
        key: this.form.key.trim(),
        name: this.form.name.trim(),
        company: { key: this.form.companyKey }
      }
      if (this.form.type && this.form.type.trim()) input.type = this.form.type.trim()
      if (this.form.city && this.form.city.trim()) input.city = this.form.city.trim()
      if (this.form.state && this.form.state.trim()) input.state = this.form.state.trim().toUpperCase()
      return input
    },
    async submitForm (bvModalEvt) {
      if (bvModalEvt) bvModalEvt.preventDefault()
      if (!this.isFormValid || this.saving) return

      this.saving = true
      const input = this.buildInput()
      try {
        if (this.formMode === 'edit') {
          await this.$apollo.mutate({
            mutation: UPDATE_SITE_MUTATION,
            variables: { id: this.editingId, input }
          })
          this.$swal.fire({ icon: 'success', title: 'Site updated.' })
        } else {
          await this.$apollo.mutate({
            mutation: CREATE_SITE_MUTATION,
            variables: { input }
          })
          this.$swal.fire({ icon: 'success', title: 'Site created.' })
        }
        this.showFormModal = false
        this.resetForm()
        this.$apollo.queries.sites.refetch()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Site save failed:', err)
        this.$swal.fire({
          icon: 'error',
          title: this.formMode === 'edit' ? 'Failed to update site' : 'Failed to create site',
          text: err.message || 'An unexpected error occurred.',
          toast: false,
          position: 'center',
          showConfirmButton: true,
          timer: 0
        })
      } finally {
        this.saving = false
      }
    },
    confirmDelete (site) {
      this.$swal.fire({
        title: 'Delete site?',
        text: `Delete "${site.name}"? Devices attached to this site will lose their site reference.`,
        icon: 'warning',
        toast: false,
        position: 'center',
        showCancelButton: true,
        showConfirmButton: true,
        timer: 0,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#dc3545'
      }).then(result => {
        if (!result.isConfirmed) return
        this.$apollo.mutate({
          mutation: DELETE_SITE_MUTATION,
          variables: { id: site.id }
        }).then(() => {
          this.$swal.fire({ icon: 'success', title: 'Site deleted.' })
          this.$apollo.queries.sites.refetch()
        }).catch(err => {
          // eslint-disable-next-line no-console
          console.error('Site delete failed:', err)
          this.$swal.fire({
            icon: 'error',
            title: 'Failed to delete site',
            text: err.message || 'An unexpected error occurred.',
            toast: false,
            position: 'center',
            showConfirmButton: true,
            timer: 0
          })
        })
      })
    }
  }
}
</script>

<style scoped>
.site-management .card-title {
  font-size: 1.1rem;
}
</style>
